// SITE-MAP-TAB-A — dedicated Site Map tab with full-height map + action rail.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Map as GoogleMap, AdvancedMarker, Pin, useMap } from '@vis.gl/react-google-maps';
import {
  ChevronDown,
  Download,
  FolderInput,
  Globe,
  Landmark,
  Layers,
  MapPin,
  Pencil,
  Satellite,
  UploadCloud,
} from 'lucide-react';
import type { SiteEntity, SiteLayer, SiteLayerCategory } from '../../types/site';
import type { SiteDocument, SiteRagReport } from '../../types/siteRag';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { VerdictPill } from '../shared/VerdictPill';
import type { DerivedSiteVerdict } from '../../lib/portfolio/deriveSiteVerdict';
import { SiteLayerOverlays, SiteBoundaryOverlay } from './SiteMapLayers';
import { useAuroraMapId, useResolvedTheme } from '../../lib/useAuroraMapId';
import { MapTypeToggle, type MapTypeToggleValue } from './MapTypeToggle';
import { SiteMapLayersPanel } from './SiteMapLayersPanel';
import { SiteStudyAreaDialog } from './SiteStudyAreaEditor';
import { LandScreeningBasis } from './LandScreeningBasis';
import { applySiteStudyArea, resolveSiteStudyArea } from '../../lib/siteStudyArea';
import { SiteMapContextMenu } from './SiteMapContextMenu';
import { SiteMapContextLayers, type ContextDatasetStatus } from './SiteMapContextLayers';
import {
  CONTEXT_DATASETS,
  contextFeatureName,
  contextFeatureToSiteLayers,
  type ContextDatasetDescriptor,
} from '../../lib/siteLayers/contextLayerCatalog';
import { useStore } from '../../store';
import type { RefdataFeature } from '../../types/refdata';
import { FitBoundsOnMount, MapResizeOnContainer } from './FitBoundsOnMount';
import { PersistSiteMapViewport } from './PersistSiteMapViewport';
import { readLastSiteMapViewport } from '../../lib/siteMapViewportMemory';
import {
  readSiteMapLayerVisibility,
  writeSiteMapLayerVisibility,
} from '../../lib/siteMapLayerVisibilityMemory';
import { pinColorsForVerdictTier } from '../../lib/btmStrategyMapLegend';
import '../../styles/auroraSurfaces.css';
import { dedupSiteLayers } from '../../lib/siteLayers/dedupSiteLayers';
import { layerCategoryKey } from './generationImpact/studio/studioLayers';
import { buildGenerationScenarioLayerGeometry } from '../../lib/generationImpact/scenarioLayerGeometry';
import { projectDocumentDerivedLayers } from '../../services/knowledge/docLayoutIntelligence';
import { materializeSiteBoundaries } from '../../lib/siteBoundaryGeometry';
import { AURORA_PARCEL_COLORS, CATEGORY_ORDER } from '../../config/siteLayerColors';
import { buildParcelDisplayEntries } from '../../lib/parcelColors';
import { toast } from 'sonner';
import { bundleFromSiteLayers } from '../../services/geoExport/layoutExportBundle';
import {
  downloadBundleAsDwg,
  downloadBundleAsDxf,
  downloadBundleAsShapefile,
  DwgExportUnavailableError,
} from '../../services/geoExport/exportBundleDownloads';

/**
 * The amber the Site Map tab has always drawn a SINGLE site boundary in.
 * Preserved for single-parcel sites so their appearance is byte-stable;
 * multi-parcel sites get distinct per-parcel hues from
 * {@link AURORA_PARCEL_COLORS} so disjoint holdings are each distinguishable.
 */
const SINGLE_PARCEL_STROKE = '#D97706';
/** Boundary fill alpha — matches the tab's prior single-boundary overlay. */
const PARCEL_FILL_OPACITY = 0.12;

/** Draft generation-layout preview renders translucent vs published (0.25). */
const DRAFT_GEN_FILL_OPACITY = 0.12;
// Stable empty sets so the draft overlay pass never re-renders on identity.
const NO_HIDDEN_CATEGORIES: ReadonlySet<string> = new Set();
const NO_HIDDEN_LAYER_IDS: ReadonlySet<string> = new Set();

/**
 * Theme-aware basemap colour scheme, resolved from the shared reactive
 * theme observer in `lib/useAuroraMapId` (safe under test — the SiteRAG
 * suites mock `./SiteMapLayers`, not `lib/`). The `colorScheme` prop keeps
 * theme-tinting the tiles until the Aurora cloud-styled vector Map IDs are
 * provisioned (`useAuroraMapId` upgrades automatically when the
 * VITE_GOOGLE_MAPS_MAP_ID_{DARK,LIGHT} env vars exist at build time — the
 * remaining half of TODO(aurora-map) is that OPS provisioning step).
 */
type MapColorScheme = 'DARK' | 'LIGHT' | 'FOLLOW_SYSTEM';

function useResolvedMapColorScheme(): MapColorScheme {
  return useResolvedTheme() === 'light' ? 'LIGHT' : 'DARK';
}

/** Valid category slugs for sanitising persisted hidden-category strings. */
const VALID_CATEGORIES = new Set<string>(CATEGORY_ORDER);

const toCategorySet = (values: readonly string[]): Set<string> =>
  new Set(values.filter((v): v is SiteLayerCategory => VALID_CATEGORIES.has(v)));

/** Geometry kinds toggleable from the layers panel's type-filter chips. */
type SiteLayerGeometryType = SiteLayer['geometry']['type'];

const VALID_GEOMETRY_TYPES: ReadonlySet<string> = new Set(['polygon', 'line', 'point']);

const toGeometryTypeSet = (values: readonly string[]): Set<SiteLayerGeometryType> =>
  new Set(values.filter((v): v is SiteLayerGeometryType => VALID_GEOMETRY_TYPES.has(v)));

/**
 * Bridge the map instance out to the overlay UI (the layers panel renders
 * OUTSIDE the `<Map>` tree, so it has no `useMap` context of its own).
 * Rendered as a Map child; hands the instance up via ref-setter.
 */
function CaptureMapInstance({ onMap }: { onMap: (map: google.maps.Map | null) => void }) {
  const map = useMap();
  useEffect(() => {
    onMap(map);
    return () => onMap(null);
  }, [map, onMap]);
  return null;
}

/**
 * Classify a Google `mapTypeId` into the custom toggle's two-value model.
 * Satellite/hybrid imagery → "satellite"; everything else (terrain/roadmap)
 * → "roadmap". Used to seed the toggle from a persisted viewport.
 */
function mapTypeChoiceFromId(mapTypeId: string | undefined): MapTypeToggleValue {
  return mapTypeId === 'satellite' || mapTypeId === 'hybrid' ? 'satellite' : 'roadmap';
}

export interface SiteMapTabProps {
  site: SiteEntity;
  siteDocuments: SiteDocument[];
  siteReports: SiteRagReport[];
  /** Same current composite authority as Home, Findings and Path to GO. */
  verdict?: DerivedSiteVerdict | null;
  onEditLocation: () => void;
  onEditBoundary: () => void;
  onUpdateKmz: () => void;
  onEditLayers: () => void;
  onUploadAreaData: () => void;
  /** Unified geo import — opens AddMapLayersDialog (KMZ / GeoJSON / SHP / DWG). */
  onAddLayers: () => void;
  /** Files dropped onto the map frame; host opens AddMapLayersDialog pre-loaded. */
  onDropFiles?: (files: File[]) => void;
  /** aurora-qa-3 (⑨) — export the site boundary as KMZ. The host owns the
   *  exporter chain (kmzExporter). Optional ⇒ surfaces without an export
   *  handler hide the control. */
  onExportKmz?: () => void;
  /** aurora-qa-3 (⑨) — open the site in Google Earth Web (KMZ menu option). */
  onOpenGoogleEarth?: () => void;
  /** Deep link into the Generation & Impact tab (layers-panel group action). */
  onOpenGenerationTab?: () => void;
}

export function SiteMapTab({
  site,
  siteDocuments,
  siteReports,
  verdict = null,
  onEditLocation,
  onEditBoundary,
  onUpdateKmz,
  onEditLayers,
  onUploadAreaData,
  onAddLayers,
  onDropFiles,
  onExportKmz,
  onOpenGoogleEarth,
  onOpenGenerationTab,
}: SiteMapTabProps) {
  const [studyAreaOpen, setStudyAreaOpen] = useState(false);
  const studyResolution = useMemo(() => {
    try { return { value: resolveSiteStudyArea(site), error: null }; }
    catch (error) { return { value: null, error: (error as Error).message }; }
  }, [site]);
  const studyFitSite = useMemo(() => studyResolution.value ? { ...applySiteStudyArea(site), siteLayers: [] } : site, [site, studyResolution.value]);
  // Layer/parcel visibility toggles — seeded from and written through to
  // per-site localStorage so the view survives reloads and site switches.
  const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(
    () => toCategorySet(readSiteMapLayerVisibility(site.id)?.hiddenCategories ?? []),
  );
  const [hiddenLayerIds, setHiddenLayerIds] = useState<Set<string>>(
    () => new Set(readSiteMapLayerVisibility(site.id)?.hiddenLayerIds ?? []),
  );
  const [hiddenParcelIds, setHiddenParcelIds] = useState<Set<string>>(
    () => new Set(readSiteMapLayerVisibility(site.id)?.hiddenParcelIds ?? []),
  );
  const [hiddenGeometryTypes, setHiddenGeometryTypes] = useState<Set<SiteLayerGeometryType>>(
    () => toGeometryTypeSet(readSiteMapLayerVisibility(site.id)?.hiddenGeometryTypes ?? []),
  );
  // Lane-2 — which refdata datasets stream as viewport context overlays.
  const [enabledContextDatasets, setEnabledContextDatasets] = useState<Set<string>>(
    () => new Set(readSiteMapLayerVisibility(site.id)?.contextDatasets ?? []),
  );
  const [contextStatuses, setContextStatuses] = useState<Readonly<Record<string, ContextDatasetStatus>>>({});
  const [categoryOpacity, setCategoryOpacity] = useState<Record<string, number>>(
    () => readSiteMapLayerVisibility(site.id)?.categoryOpacity ?? {},
  );
  const [layerOrder, setLayerOrder] = useState<string[]>(
    () => readSiteMapLayerVisibility(site.id)?.layerOrder ?? [],
  );
  // Which site the current Sets belong to — blocks the write-through effect
  // from persisting the PREVIOUS site's Sets under a new site's key during
  // the one render where site.id changed but the re-seed effect hasn't run.
  const visibilitySeededFor = useRef<string>(site.id);

  useEffect(() => {
    if (visibilitySeededFor.current === site.id) return;
    const saved = readSiteMapLayerVisibility(site.id);
    setHiddenCategories(toCategorySet(saved?.hiddenCategories ?? []));
    setHiddenLayerIds(new Set(saved?.hiddenLayerIds ?? []));
    setHiddenParcelIds(new Set(saved?.hiddenParcelIds ?? []));
    setHiddenGeometryTypes(toGeometryTypeSet(saved?.hiddenGeometryTypes ?? []));
    setEnabledContextDatasets(new Set(saved?.contextDatasets ?? []));
    setCategoryOpacity(saved?.categoryOpacity ?? {});
    setLayerOrder(saved?.layerOrder ?? []);
    visibilitySeededFor.current = site.id;
  }, [site.id]);

  useEffect(() => {
    if (visibilitySeededFor.current !== site.id) return;
    writeSiteMapLayerVisibility(site.id, {
      hiddenCategories: [...hiddenCategories],
      hiddenLayerIds: [...hiddenLayerIds],
      hiddenParcelIds: [...hiddenParcelIds],
      hiddenGeometryTypes: [...hiddenGeometryTypes],
      contextDatasets: [...enabledContextDatasets],
      categoryOpacity,
      layerOrder,
    });
  }, [site.id, hiddenCategories, hiddenLayerIds, hiddenParcelIds, hiddenGeometryTypes, enabledContextDatasets, categoryOpacity, layerOrder]);

  const toggleCategory = useCallback((cat: string) => {
    setHiddenCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }, []);

  const toggleLayer = useCallback((id: string) => {
    setHiddenLayerIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleParcel = useCallback((id: string) => {
    setHiddenParcelIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleGeometryType = useCallback((type: SiteLayerGeometryType) => {
    setHiddenGeometryTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const resetLayers = useCallback(() => {
    setHiddenCategories(new Set());
    setHiddenLayerIds(new Set());
    setHiddenParcelIds(new Set());
    setHiddenGeometryTypes(new Set());
  }, []);

  const toggleContextDataset = useCallback((id: string) => {
    setEnabledContextDatasets(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Lane-2 → lane-1 bridge: promote ONE streamed feature into the saved
  // (evidence-grade) layer set. Runs through the same converter + merge as a
  // regional fetch, so ids/hashes line up and later fetches dedup cleanly.
  const updateSiteLayers = useStore(s => s.updateSiteLayers);
  const promoteContextFeature = useCallback(async (
    descriptor: ContextDatasetDescriptor,
    feature: RefdataFeature,
  ) => {
    try {
      const layers = contextFeatureToSiteLayers(descriptor, feature);
      if (layers.length === 0) {
        toast.error('This feature has no drawable geometry.');
        return;
      }
      const { mergeImportedLayers } = await import('../../services/kmzParser');
      const { merged, added } = mergeImportedLayers(site.siteLayers ?? [], layers);
      if (added.length === 0) {
        toast.info('Already saved to site layers.');
        return;
      }
      await updateSiteLayers(site.id, merged);
      toast.success(`Saved "${contextFeatureName(descriptor, feature.props)}" to site layers.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save the feature.');
    }
  }, [site.id, site.siteLayers, updateSiteLayers]);

  const mapLayers = useMemo(() => {
    const merged = [...(site.siteLayers ?? []), ...projectDocumentDerivedLayers(site, siteDocuments)];
    return dedupSiteLayers({ siteId: site.id, layers: merged }).layers;
  }, [site, siteDocuments]);

  // P4 — persisted draw order. Array order = z-order (last on top); ids not
  // in the saved order keep their natural position after the ordered ones.
  const orderedMapLayers = useMemo(() => {
    if (layerOrder.length === 0) return mapLayers;
    const pos = new Map(layerOrder.map((id, i) => [id, i]));
    return mapLayers
      .map((l, i) => ({ l, key: pos.get(l.id) ?? layerOrder.length + i }))
      .sort((a, b) => a.key - b.key)
      .map(({ l }) => l);
  }, [mapLayers, layerOrder]);

  const moveLayer = useCallback((id: string, direction: 'up' | 'down') => {
    const current = orderedMapLayers.map(l => l.id);
    const idx = current.indexOf(id);
    if (idx < 0) return;
    const cat = orderedMapLayers[idx].category;
    // Swap with the nearest neighbor in the SAME category so panel rows
    // (grouped by category) move exactly one visible step.
    const step = direction === 'up' ? -1 : 1;
    let j = idx + step;
    while (j >= 0 && j < orderedMapLayers.length && orderedMapLayers[j].category !== cat) j += step;
    if (j < 0 || j >= orderedMapLayers.length) return;
    const next = [...current];
    [next[idx], next[j]] = [next[j], next[idx]];
    setLayerOrder(next);
  }, [orderedMapLayers]);

  const setCategoryOpacityValue = useCallback((cat: string, value: number) => {
    setCategoryOpacity(prev => ({ ...prev, [cat]: value }));
  }, []);

  // Zoom-to-layer + category solo (layers-panel row/header actions).
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const captureMapInstance = useCallback((map: google.maps.Map | null) => {
    mapInstanceRef.current = map;
  }, []);

  const zoomToLayer = useCallback((layer: SiteLayer) => {
    const map = mapInstanceRef.current;
    const coords = layer.geometry.coordinates;
    if (!map || coords.length === 0 || typeof google === 'undefined' || !google.maps) return;
    if (layer.geometry.type === 'point' || coords.length === 1) {
      map.setCenter(coords[0]);
      map.setZoom(16);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    for (const c of coords) bounds.extend(c);
    map.fitBounds(bounds, 48);
  }, []);

  const soloCategory = useCallback((cat: string) => {
    setHiddenCategories(prev => {
      const present = new Set<string>(mapLayers.map(layerCategoryKey));
      const others = [...present].filter(c => c !== cat);
      const alreadySoloed = others.length > 0
        && others.every(c => prev.has(c))
        && !prev.has(cat);
      // Second click on the soloed category restores everything.
      return alreadySoloed ? new Set() : new Set(others);
    });
  }, [mapLayers]);

  // MP (multi-parcel) — the canonical parcel list. A KMZ with N disjoint
  // parcels lands here as N sanitized parcels (`site.siteBoundaries[]`); a
  // legacy single-polygon site lifts to exactly one parcel whose polygon
  // equals `site.siteBoundary` (byte-stable single-parcel behaviour). This
  // replaces the old single-`site.siteBoundary` render path that drew only
  // ONE polygon regardless of how many parcels the site actually had.
  const parcels = useMemo(
    () => materializeSiteBoundaries({
      siteBoundaries: site.siteBoundaries,
      siteBoundary: site.siteBoundary,
      siteArea: site.siteArea,
      sitePerimeter: site.sitePerimeter,
      siteCentroid: site.siteCentroid,
    }),
    [site.siteBoundaries, site.siteBoundary, site.siteArea, site.sitePerimeter, site.siteCentroid],
  );

  // Display-ready parcel entries (disambiguated name + swatch color) shared
  // by BOTH the drawn outline and the layer-panel row so map == legend
  // one-for-one. Single-parcel keeps the historical amber; multi-parcel uses
  // the distinct Aurora per-parcel palette.
  const parcelEntries = useMemo(() => {
    const multi = parcels.length > 1;
    return buildParcelDisplayEntries(parcels).map((entry, i) => ({
      ...entry,
      color: multi
        ? AURORA_PARCEL_COLORS[i % AURORA_PARCEL_COLORS.length].dot
        : SINGLE_PARCEL_STROKE,
    }));
  }, [parcels]);

  // Draft (promoted-but-unpublished) Generation layout preview. Available
  // when a promoted scenario exists whose geometry isn't what the current
  // publication froze (different scenario, edited since publish, or nothing
  // published yet). Never persisted — derived render state only, computed
  // lazily when the panel toggle enables it.
  const promotedSnapshot = site.promotedGenerationScenarioSnapshot;
  const layoutHead = site.generationImpactLayoutHead;
  const draftGenAvailable = Boolean(promotedSnapshot) && (
    !layoutHead
    || layoutHead.scenarioId !== promotedSnapshot!.id
    || layoutHead.scenarioUpdatedAt !== promotedSnapshot!.updatedAt
  );
  const [draftGenEnabled, setDraftGenEnabled] = useState(false);
  const draftGen = useMemo(() => {
    if (!draftGenEnabled || !draftGenAvailable || !promotedSnapshot) return null;
    try {
      const { layers } = buildGenerationScenarioLayerGeometry(promotedSnapshot, parcels, {
        importedAt: promotedSnapshot.updatedAt,
        idPrefix: 'draft-gen',
      });
      return { layers, error: null as string | null };
    } catch (e) {
      return { layers: [] as SiteLayer[], error: e instanceof Error ? e.message : String(e) };
    }
  }, [draftGenEnabled, draftGenAvailable, promotedSnapshot, parcels]);

  const totalLayers = mapLayers.length;
  // Each materialized parcel is guaranteed ≥3 sanitized vertices, so a
  // non-empty parcel list IS an exportable boundary (matches the kmzExporter
  // guard the host applies). `hasGeometry` is the broader "show the map" test.
  const hasBoundary = parcels.length > 0;
  const hasGeometry = Boolean(
    site.location
    || hasBoundary
    || totalLayers > 0,
  );

  // A historical report's narrative tier cannot supply a current site decision.
  const bandColor = verdict?.band ? verdict.bandColor : 'gray';
  const pinColors = bandColor === 'orange'
    ? { background: '#ea580c', borderColor: '#9a3412' }
    : pinColorsForVerdictTier(bandColor === 'green' || bandColor === 'amber' || bandColor === 'red' ? bandColor : 'none');
  // Theme-aware basemap: dark chrome → dark tiles, light chrome → light tiles.
  const mapColorScheme = useResolvedMapColorScheme();
  // Aurora vector Map ID (falls back to the legacy default until the
  // per-theme cloud-styled Map IDs are provisioned — see useAuroraMapId).
  const auroraMapId = useAuroraMapId();

  // Drag-drop geo files (KMZ / GeoJSON / SHP / DWG) straight onto the map.
  const [frameDragOver, setFrameDragOver] = useState(false);
  const dragHasFiles = (e: React.DragEvent): boolean =>
    Array.from(e.dataTransfer.types).includes('Files');

  const defaultCenter = site.siteCentroid ?? site.location ?? { lat: 0, lng: 0 };
  const savedViewport = useMemo(() => readLastSiteMapViewport(site.id), [site.id]);
  const initialCenter = savedViewport?.center ?? defaultCenter;
  const initialZoom = savedViewport?.zoom ?? 14;
  const initialMapTypeId = savedViewport?.mapTypeId ?? 'terrain';

  // Controlled map-type driven by the custom Aurora <MapTypeToggle> (replaces
  // the native Google "Map | Satellite" control). Seeded from the persisted
  // viewport so the surface restores the user's last choice. Within a category
  // we keep the persisted concrete id; flipping category resolves to the
  // surface defaults ("Map" → terrain, "Satellite" → hybrid).
  const [mapTypeId, setMapTypeId] = useState<string>(initialMapTypeId);
  const mapTypeChoice = mapTypeChoiceFromId(mapTypeId);
  const handleMapTypeChange = useCallback((next: MapTypeToggleValue) => {
    setMapTypeId(next === 'satellite' ? 'hybrid' : 'terrain');
  }, []);

  return (
    // SITE-MAP-TAB-A — the map owns the whole tab body, FULL-BLEED. No card box,
    // no TabHeader; all controls are floating-glass overlays inside the frame.
    <div
      className="relative h-full min-h-[24rem] w-full max-w-full flex-1 overflow-hidden bg-state-hover"
      data-testid="site-map-tab"
    >
      <div
        className="relative h-full w-full"
        data-testid="site-map-tab-frame"
        onDragOver={(e) => {
          if (!onDropFiles || !dragHasFiles(e)) return;
          e.preventDefault();
          setFrameDragOver(true);
        }}
        onDragLeave={() => setFrameDragOver(false)}
        onDrop={(e) => {
          if (!onDropFiles || !dragHasFiles(e)) return;
          e.preventDefault();
          setFrameDragOver(false);
          const files = Array.from(e.dataTransfer.files);
          if (files.length > 0) onDropFiles(files);
        }}
      >
        {hasGeometry ? (
          <div className="absolute inset-0">
            <GoogleMap
              key={site.id}
              // Aurora vector Map ID (theme-reactive). Until the styled Map
              // IDs are provisioned (OPS follow-up), this resolves to the
              // legacy id and `colorScheme` keeps theme-tinting the tiles.
              mapId={auroraMapId}
              colorScheme={mapColorScheme}
              defaultCenter={initialCenter}
              defaultZoom={initialZoom}
              gestureHandling="cooperative"
              mapTypeId={mapTypeId}
              // Native map-type ("Map | Satellite") control replaced by the
              // custom Aurora <MapTypeToggle> in the top-left glass cluster.
              mapTypeControl={false}
              fullscreenControl
              streetViewControl={false}
              style={{ width: '100%', height: '100%' }}
            >
              {/* Fit to the UNION of every parcel (not just parcel[0]) so
                  disjoint holdings all sit in frame. */}
              <FitBoundsOnMount
                key={studyResolution.value?.scope.selectionHash ?? 'legacy'}
                site={studyFitSite}
                parcels={studyResolution.value?.parcels ?? parcels}
                skipFit={!studyResolution.value && savedViewport != null}
              />
              <PersistSiteMapViewport siteId={site.id} />
              <MapResizeOnContainer />
              <CaptureMapInstance onMap={captureMapInstance} />
              {site.location && (
                <AdvancedMarker position={site.location}>
                  <Pin
                    background={pinColors.background}
                    glyphColor="#fff"
                    borderColor={pinColors.borderColor}
                  />
                </AdvancedMarker>
              )}
              {/* Draw EVERY parcel as its own polygon (distinct color when
                  multi-parcel), honouring the per-parcel visibility toggles.
                  Hidden parcels unmount → their overlay is removed. */}
              {parcels.map((parcel, i) => {
                if (hiddenParcelIds.has(parcel.id)) return null;
                return (
                  <SiteBoundaryOverlay
                    key={parcel.id}
                    path={parcel.polygon}
                    strokeColor={parcelEntries[i]?.color ?? SINGLE_PARCEL_STROKE}
                    fillOpacity={PARCEL_FILL_OPACITY}
                  />
                );
              })}
              {totalLayers > 0 && (
                <SiteLayerOverlays
                  layers={orderedMapLayers}
                  hiddenCategories={hiddenCategories}
                  hiddenLayerIds={hiddenLayerIds}
                  hiddenGeometryTypes={hiddenGeometryTypes}
                  fillOpacity={0.25}
                  categoryOpacity={categoryOpacity}
                />
              )}
              {/* Draft generation layout — translucent preview of the
                  promoted-but-unpublished scenario; never persisted. */}
              {draftGen && draftGen.layers.length > 0 && (
                <SiteLayerOverlays
                  layers={draftGen.layers}
                  hiddenCategories={NO_HIDDEN_CATEGORIES}
                  hiddenLayerIds={NO_HIDDEN_LAYER_IDS}
                  fillOpacity={DRAFT_GEN_FILL_OPACITY}
                />
              )}
              {/* Lane-2 — viewport-streamed context overlays (display-only). */}
              {enabledContextDatasets.size > 0 && (
                <SiteMapContextLayers
                  enabledDatasetIds={enabledContextDatasets}
                  onStatusChange={setContextStatuses}
                  onPromoteFeature={promoteContextFeature}
                />
              )}
            </GoogleMap>
          </div>
        ) : (
          <div
            className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-text-secondary"
            data-testid="site-map-tab-empty"
          >
            <MapPin className="h-8 w-8 text-text-muted" aria-hidden />
            <p className="text-sm">No site location or boundary yet.</p>
            <p className="text-xs text-text-muted">Use the controls to set a location, draw a boundary, or upload area data.</p>
            <Button
              variant="outline"
              className="da-btn mt-2"
              onClick={onAddLayers}
              data-testid="site-map-empty-add-layers"
            >
              <UploadCloud aria-hidden />
              Add layers
            </Button>
          </div>
        )}

        {/* Drop-target hint while dragging geo files over the frame. */}
        {frameDragOver && (
          <div
            className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center border-2 border-dashed border-primary bg-black/40"
            data-testid="site-map-drop-hint"
          >
            <div className="map-glass px-4 py-2 type-section text-text-primary">
              Drop files to add map layers (KMZ · GeoJSON · SHP · DWG)
            </div>
          </div>
        )}

        {/* 1 · TOP-LEFT — floating-glass action cluster + map-type toggle */}
        <div className="map-glass map-ctrl-tl" data-testid="site-map-tab-rail">
          <div className="map-ctrl-eyebrow">
            <span className="dot" aria-hidden />
            <span className="lbl">Map</span>
            {hasGeometry && (
              <span className="lbl ctx" data-testid="site-map-layer-summary-total">
                · {totalLayers} layer{totalLayers === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <Button variant="outline" className="da-btn map-action" onClick={() => setStudyAreaOpen(true)} data-testid="site-map-study-area">
            <Landmark aria-hidden />
            {site.studyArea ? 'Review study area' : 'Define study area'}
          </Button>
          {studyResolution.value && <Button variant="outline" className="da-btn map-action" data-testid="site-map-zoom-study-area" onClick={() => {
            const map = mapInstanceRef.current;
            if (!map) return;
            const bounds = new google.maps.LatLngBounds();
            studyResolution.value!.parcels.forEach(parcel => parcel.polygon.forEach(point => bounds.extend(point)));
            map.fitBounds(bounds, 48);
          }}>Zoom to study area</Button>}
          {studyResolution.error && <p className="max-w-52 p-2 text-xs text-amber-700" role="status">Study area needs review: a selected polygon is missing, changed, or not selected.</p>}

          {/* Consolidated rail — 8 flat buttons became three grouped menus
              (Import / Edit / Export). Menu items keep the historical
              data-testids so host wiring + tests stay 1:1 traceable. */}
          <DropdownMenu>
            <DropdownMenuTrigger
              className="da-btn map-action"
              data-testid="site-map-menu-import"
            >
              <FolderInput aria-hidden />
              <span className="flex-1 text-left">Import</span>
              <ChevronDown aria-hidden />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[13rem]">
              <DropdownMenuItem onClick={onAddLayers} data-testid="site-map-add-layers">
                <UploadCloud aria-hidden />
                Add layers (KMZ · SHP · GeoJSON · DWG)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onUpdateKmz} data-testid="site-map-update-kmz">
                <Landmark aria-hidden />
                Import boundary (KMZ)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onUploadAreaData} data-testid="site-map-upload-area-data">
                <Satellite aria-hidden />
                Fetch regional data
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Context streaming is a DISPLAY control (like the basemap), not
              site evidence — it lives on this rail below Import (2026-08-18,
              William) and the bottom-left panel stays purely "Site layers".
              Always shown (no hasGeometry gate, William 2026-08-18): the
              toggles are discoverable even before a location is set; datasets
              simply report their status once a viewport exists. */}
          <SiteMapContextMenu
            datasets={CONTEXT_DATASETS.map(d => ({
              id: d.datasetId,
              label: d.label,
              color: d.color,
              enabled: enabledContextDatasets.has(d.datasetId),
              status: contextStatuses[d.datasetId],
            }))}
            onToggle={toggleContextDataset}
          />

          <DropdownMenu>
            <DropdownMenuTrigger
              className="da-btn map-action"
              data-testid="site-map-menu-edit"
            >
              <Pencil aria-hidden />
              <span className="flex-1 text-left">Edit</span>
              <ChevronDown aria-hidden />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[13rem]">
              <DropdownMenuItem onClick={() => setStudyAreaOpen(true)} data-testid="site-map-define-study-area">
                <Landmark aria-hidden />
                Define study area
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onEditLocation} data-testid="site-map-edit-location">
                <MapPin aria-hidden />
                Edit location
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onEditBoundary} data-testid="site-map-edit-boundary">
                <Pencil aria-hidden />
                Edit boundary
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onEditLayers} data-testid="site-map-edit-layers">
                <Layers aria-hidden />
                Edit layers
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* aurora-qa-3 (⑨) — export affordances; the host owns the export /
              Earth-Web handlers. Export stays disabled until the site has a
              ≥3-vertex boundary to export. */}
          {(onExportKmz || onOpenGoogleEarth) && (
            <DropdownMenu>
              <DropdownMenuTrigger
                className="da-btn map-action"
                data-testid="site-map-menu-export"
              >
                <Download aria-hidden />
                <span className="flex-1 text-left">Export</span>
                <ChevronDown aria-hidden />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[13rem]">
                {onExportKmz && (
                  <DropdownMenuItem
                    onClick={onExportKmz}
                    disabled={!hasBoundary}
                    title={!hasBoundary ? 'Draw or upload a site boundary (≥3 points) to export a KMZ.' : undefined}
                    data-testid="site-map-export-kmz"
                  >
                    <Download aria-hidden />
                    Export KMZ
                  </DropdownMenuItem>
                )}
                {/* Layer exports build in the browser from the shared
                    layoutExportBundle; DWG round-trips our DXF through the
                    server converter with a graceful DXF-advice fallback. */}
                {(['shapefile', 'dxf', 'dwg'] as const).map((format) => (
                  <DropdownMenuItem
                    key={format}
                    disabled={!hasBoundary && (site.siteLayers ?? []).length === 0}
                    title={!hasBoundary && (site.siteLayers ?? []).length === 0 ? 'Add a site boundary or map layers to export them.' : undefined}
                    onClick={() => {
                      void (async () => {
                        try {
                          const bundle = bundleFromSiteLayers(site.siteLayers ?? [], `${site.name ?? 'site'}-layers`, parcels);
                          const filename = format === 'shapefile'
                            ? await downloadBundleAsShapefile(bundle)
                            : format === 'dxf'
                              ? downloadBundleAsDxf(bundle)
                              : await downloadBundleAsDwg(bundle);
                          toast.success(`Exported ${filename}.`);
                        } catch (error) {
                          if (error instanceof DwgExportUnavailableError) {
                            toast.warning(error.message);
                            return;
                          }
                          toast.error(error instanceof Error ? error.message : 'Export failed.');
                        }
                      })();
                    }}
                    data-testid={`site-map-export-${format}`}
                  >
                    <Download aria-hidden />
                    Export {format === 'shapefile' ? 'Shapefile' : format.toUpperCase()}
                  </DropdownMenuItem>
                ))}
                {onOpenGoogleEarth && (
                  <DropdownMenuItem
                    onClick={onOpenGoogleEarth}
                    disabled={!site.location}
                    title={!site.location ? 'Set a site location to open Google Earth.' : undefined}
                    data-testid="site-map-open-google-earth"
                  >
                    <Globe aria-hidden />
                    Open in Google Earth
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {hasGeometry && (
            <>
              <div className="map-ctrl-group-label">Basemap</div>
              <MapTypeToggle
                value={mapTypeChoice}
                onChange={handleMapTypeChange}
                block
                className="!static !shadow-none"
              />
            </>
          )}
        </div>

        {/* 2 · BOTTOM-LEFT — grouped interactive legend. Shows for layers OR a
            MULTI-parcel site (a pure multi-parcel KMZ has no siteLayers, but
            its parcels must still be listed + toggleable). Single-parcel sites
            are unchanged: no "Parcels" group (mirrors SiteBoundaryParcelLegend,
            which collapses a single parcel to a caption), so the panel behaves
            exactly as before. */}
        {(totalLayers > 0 || parcels.length > 1 || draftGenAvailable) && hasGeometry && (
          <SiteMapLayersPanel
            layers={orderedMapLayers}
            parcels={parcels.length > 1 ? parcelEntries : undefined}
            hiddenCategories={hiddenCategories}
            hiddenLayerIds={hiddenLayerIds}
            hiddenParcelIds={hiddenParcelIds}
            hiddenGeometryTypes={hiddenGeometryTypes}
            onToggleCategory={toggleCategory}
            onToggleLayer={toggleLayer}
            onToggleParcel={toggleParcel}
            onToggleGeometryType={toggleGeometryType}
            onReset={resetLayers}
            onZoomToLayer={zoomToLayer}
            onSoloCategory={soloCategory}
            categoryOpacity={categoryOpacity}
            onSetCategoryOpacity={setCategoryOpacityValue}
            onMoveLayer={moveLayer}
            generationHead={layoutHead
              ? { scenarioName: layoutHead.scenarioName, publishedAt: layoutHead.publishedAt }
              : null}
            onOpenGeneration={onOpenGenerationTab}
            draftLayout={draftGenAvailable
              ? {
                  enabled: draftGenEnabled,
                  onToggle: () => setDraftGenEnabled(v => !v),
                  count: draftGen ? draftGen.layers.length : null,
                  error: draftGen?.error ?? null,
                }
              : null}
            position="bottom-left"
          />
        )}

        {/* 3 · BOTTOM-RIGHT — site context + BTM verdict chip */}
        <div className="map-glass map-ctrl-br" data-testid="site-map-layer-summary">
          <div className="map-site-chip">
            <span className="nm">{site.name}</span>
            <VerdictPill verdict={verdict} showComposite={false} data-testid="site-map-verdict" />
          </div>
          {(site.address || totalLayers > 0) && (
            <div className="map-site-sub">
              {[site.address, totalLayers > 0 ? `${totalLayers} layer${totalLayers === 1 ? '' : 's'}` : null]
                .filter(Boolean)
                .join(' · ')}
            </div>
          )}
          {studyResolution.value && (
            <div className="map-site-sub" data-testid="site-map-study-area-summary">
              {studyResolution.value.scope.name} · {Math.round(studyResolution.value.scope.grossAreaAcres).toLocaleString()} gross acres selected
            </div>
          )}
          <LandScreeningBasis site={site} compact className="mt-2 max-w-xs" />
        </div>
      </div>
      {studyAreaOpen && <SiteStudyAreaDialog site={site} onClose={() => setStudyAreaOpen(false)} />}
    </div>
  );
}
